/**
 * How many leading segments name a screen, by first segment. The rest are ids or the `:microService` of
 * a destination, which belong to the platform being left. Unlisted sections use one segment.
 */
const SECTION_DEPTH: Readonly<Record<string, number>> = {
  'ai-admin': 2,
  security: 2,
};

/**
 * The same screen in another platform: `/p/harley/security/users` → `/p/overa/security/users`.
 *
 * The URL is reduced to the screen's list page, because what comes after it (an id, a destination
 * microservice) is data of the platform being left and would not exist, or would mean something else, in
 * the target. The query string goes too: it usually carries a tenant.
 *
 * @param url Current router URL.
 * @param to Target platform code.
 * @param unavailable First segments of the screens the target does not offer (see the menu surfaces);
 *   landing on one of them goes to the target's overview instead.
 */
export function switchPlatformUrl(
  url: string,
  to: string,
  unavailable: ReadonlySet<string> = new Set(),
): string {
  const target = `/p/${encodeURIComponent(to)}`;
  const segments = url.split(/[?#]/)[0].split('/').filter(Boolean);

  // Outside a platform, or at its root: the target's landing page.
  if (segments[0] !== 'p' || segments.length < 3) return target;

  const rest = segments.slice(2);
  if (unavailable.has(rest[0])) return `${target}/overview`;

  const depth = SECTION_DEPTH[rest[0]] ?? 1;
  return `${target}/${rest.slice(0, depth).join('/')}`;
}
